import type { EmailNotificationConfig, NotificationAudience } from ".";
import { collectNamedFormElements } from "./form-schema-walk";
import { formValidationPatterns } from "./validate-form-submission";

/** ACS's limit for To + CC + BCC on one message. */
export const MAX_EMAIL_RECIPIENTS = 50;

export interface EmailDelivery {
	audience: NotificationAudience;
	bcc?: string[];
	cc?: string[];
	replyTo?: string;
	to: string[];
}

/**
 * The client's address: the Email field named `fieldName` when it still exists, else the
 * form's first `fieldType: "email"` field — only if the value is a valid address.
 */
export function findSubmitterEmail(
	schemaRoot: unknown,
	data: Record<string, unknown>,
	fieldName?: string
): string | null {
	const emailFields = collectNamedFormElements(schemaRoot).filter(
		(f) => f.type === "form_field" && f.fieldType === "email"
	);
	const field =
		(fieldName && emailFields.find((f) => f.name === fieldName)) ||
		emailFields[0];
	const raw = field?.name ? data[field.name] : undefined;
	const value = typeof raw === "string" ? raw.trim() : "";
	return value && formValidationPatterns.EMAIL_PATTERN.test(value)
		? value
		: null;
}

/** To first, then CC, then BCC; an address keeps its first place only; 50 in total. */
function splitRecipients(n: EmailNotificationConfig): {
	bcc: string[];
	cc: string[];
	to: string[];
} {
	const seen = new Set<string>();
	const take = (list: string[] | undefined) => {
		const out: string[] = [];
		for (const address of list ?? []) {
			const trimmed = address.trim();
			const key = trimmed.toLowerCase();
			if (trimmed && !seen.has(key) && seen.size < MAX_EMAIL_RECIPIENTS) {
				seen.add(key);
				out.push(trimmed);
			}
		}
		return out;
	};
	const to = take(n.to);
	const cc = take(n.cc);
	return { to, cc, bcc: take(n.bcc) };
}

/**
 * Who one email entry goes to, or `null` when it must not send:
 * switched off, no team To address, or no valid client address.
 */
export function resolveEmailDelivery(
	notification: EmailNotificationConfig,
	submitterEmail: string | null
): EmailDelivery | null {
	if (notification.mode === "off") {
		return null;
	}
	const audience = notification.audience ?? "admin";
	const replyTo = notification.replyTo?.trim();
	if (audience === "submitter") {
		return submitterEmail
			? { audience, to: [submitterEmail], ...(replyTo ? { replyTo } : {}) }
			: null;
	}
	const { to, cc, bcc } = splitRecipients(notification);
	if (to.length === 0) {
		return null;
	}
	const reply = replyTo || submitterEmail;
	return {
		audience,
		to,
		...(cc.length ? { cc } : {}),
		...(bcc.length ? { bcc } : {}),
		...(reply ? { replyTo: reply } : {}),
	};
}
